// `/warmup/:code` (UI-6): one pool's warmup. The curve — what went out, solid;
// the scheduler's own ramp from the day the pool started, dashed; now, ringed —
// then every day with its budget, figures and decision, and the pool's log:
// rule 6's answer to why the limit moved or stopped.

import { useParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import type { WarmupDay, WarmupEvent } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/emc/DataTable";
import { PageHeader } from "@/components/emc/PageHeader";
import { StatGrid, StatTile } from "@/components/emc/StatTile";
import { StatusPill } from "@/components/emc/StatusPill";
import { Curve } from "@/components/emc/charts/Curve";
import { LimitBar } from "@/components/emc/charts/LimitBar";
import { EmptyState, ErrorState, LoadingState } from "@/components/emc/states";
import { count, shareOf } from "@/components/emc/format";
import { labelOf } from "@/components/emc/status";
import { PoolActions } from "@/pages/warmup/actions";
import { usePoolDetail } from "@/pages/warmup/data";
import {
  budgetLook,
  compact,
  curveData,
  dayLabel,
  eventWhat,
  eventWhy,
  heldLook,
  ispShares,
  planLines,
  rateCell,
  stepText,
  thresholdLine,
  todaySoFar,
} from "@/pages/warmup/model";
import { DecisionLine, RatePill, StoppedNotice } from "@/pages/warmup/parts";
import { dayStartText, shortAtViewer } from "@/components/emc/time";

function dayColumns(minSample: number): ColumnDef<WarmupDay>[] {
  return [
    {
      id: "day",
      header: "Nap",
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span className="data-mono">{dayLabel(row.original.day)}</span>
          {row.original.today && <span className="text-label text-muted-foreground">ma, élő</span>}
          {!row.original.today && row.original.figures.partial && (
            <span className="text-label text-warn" title="A reggeli újraszámolás a nap vége előtt futott.">
              részleges
            </span>
          )}
        </span>
      ),
    },
    {
      id: "budget",
      header: "Keret",
      cell: ({ row }) => {
        const held = heldLook(row.original.held);
        return (
          <span className="flex flex-col items-end">
            <span>{count(row.original.budget)}</span>
            {held && (
              <span
                className={held.tone === "danger" ? "text-label text-danger" : "text-label text-warn"}
                title="A nap egy részében a pool állt vagy az őrszem lassította: a limiter ennél kevesebbet engedett."
              >
                {held.label}
              </span>
            )}
          </span>
        );
      },
      meta: { align: "right" },
    },
    {
      id: "sent",
      header: "Kiment",
      cell: ({ row }) => {
        const rates = row.original.figures.rates;
        if (rates === null) return <span className="text-muted-foreground">nincs adat</span>;
        const ofBudget = shareOf(rates.sent, row.original.budget, "a keret");
        return (
          <span className="flex flex-col items-end">
            <span>{count(rates.sent)}</span>
            {ofBudget && <span className="text-label text-muted-foreground">{ofBudget}</span>}
          </span>
        );
      },
      meta: { align: "right" },
    },
    {
      id: "delivered",
      header: "Kézbesítve",
      cell: ({ row }) => {
        const rates = row.original.figures.rates;
        return rates === null ? "—" : count(rates.delivered);
      },
      meta: { align: "right" },
    },
    {
      id: "bounce",
      header: "Bounce",
      cell: ({ row }) => <RatePill rates={row.original.figures.rates} kind="bounce" minSample={minSample} />,
    },
    {
      id: "complaint",
      header: "Panasz",
      cell: ({ row }) => (
        <RatePill rates={row.original.figures.rates} kind="complaint" minSample={minSample} />
      ),
    },
    {
      id: "decision",
      header: "Döntés és indok",
      cell: ({ row }) =>
        row.original.decision ? (
          <DecisionLine decision={row.original.decision} />
        ) : (
          <span className="text-muted-foreground">
            {row.original.held === "paused"
              ? "nincs döntés — a pool állt, az ütemező álló poolról nem dönt"
              : "nincs döntés-sor — az előző limit élt"}
          </span>
        ),
      meta: { wrap: true, className: "min-w-72" },
    },
  ];
}

function eventColumns(): ColumnDef<WarmupEvent>[] {
  return [
    {
      id: "when",
      header: "Mikor",
      cell: ({ row }) => shortAtViewer(row.original.occurred_at),
      meta: { mono: true, muted: true },
    },
    {
      id: "source",
      header: "Napló",
      cell: ({ row }) => labelOf("poolLog", row.original.source),
    },
    {
      id: "what",
      header: "Mi történt",
      cell: ({ row }) => {
        const event = row.original;
        return (
          <span className="flex flex-wrap items-center gap-2">
            {event.source === "alert" && event.level && <StatusPill domain="alert" state={event.level} />}
            <span className={event.source === "alert" ? "data-mono text-label" : undefined}>
              {eventWhat(event)}
            </span>
            {event.per_ip !== null && event.source !== "alert" && (
              <span className="text-label text-muted-foreground">{count(event.per_ip)} / IP</span>
            )}
            {event.rehearsal && <span className="text-label font-medium text-warn">próba</span>}
          </span>
        );
      },
      meta: { wrap: true },
    },
    {
      id: "actor",
      header: "Ki",
      cell: ({ row }) =>
        row.original.source === "alert"
          ? row.original.acknowledged
            ? `nyugtázta: ${row.original.actor}`
            : "—"
          : row.original.actor,
      meta: { muted: true },
    },
    {
      id: "why",
      header: "Miért",
      cell: ({ row }) => eventWhy(row.original),
      meta: { wrap: true },
    },
  ];
}

export default function PoolDetail() {
  const { code = "" } = useParams();
  const detail = usePoolDetail(code.toUpperCase());
  const data = detail.data;

  if (data === null) {
    return (
      <>
        <PageHeader title={`${code.toUpperCase()} pool`} back={{ to: "/warmup", label: "Warmup" }} />
        {detail.error !== null ? (
          <ErrorState message={detail.error} onRetry={() => void detail.reload()} />
        ) : (
          <LoadingState label="A pool betöltése…" />
        )}
      </>
    );
  }

  const { pool, days, events } = data;
  const zone = pool.timezone;
  const minSample = days.knobs.min_sample;
  const look = budgetLook(pool);
  const sent = pool.today.rates?.sent ?? 0;
  const points = curveData(days.curve.points, days.today);
  const yesterday = pool.yesterday;
  const bounce = rateCell(yesterday.rates, "bounce", minSample);
  const complaint = rateCell(yesterday.rates, "complaint", minSample);
  const isps = ispShares(yesterday.by_isp);

  return (
    <>
      <PageHeader
        back={{ to: "/warmup", label: "Warmup" }}
        title={
          <span>
            <span className="data-mono">{pool.code}</span> pool
          </span>
        }
        documentTitle={`${pool.code} pool · Warmup`}
        meta={
          <>
            <StatusPill domain="warmup" state={pool.warmup_state} />
            <StatusPill domain="poolWatch" state={pool.watch.state} />
          </>
        }
        description={
          <>
            {pool.name} · {pool.ip_count} IP · SES {pool.ses_account} ·{" "}
            <span className="data-mono">{pool.countries.join(" ")}</span> · idők:{" "}
            <span className="data-mono" title={pool.timezones.join(", ")}>
              {zone}
            </span>
            {pool.timezones.length > 1 && ` (a pool zónái: ${pool.timezones.join(", ")})`}
          </>
        }
        actions={
          <PoolActions pool={pool} knobs={days.knobs} onChanged={() => void detail.reload()} size="default" />
        }
      />

      <div className="flex flex-col gap-6">
        {detail.error !== null && <ErrorState message={detail.error} onRetry={() => void detail.reload()} />}
        <StoppedNotice pool={pool} />

        <StatGrid>
          <StatTile
            label="Ma kiment"
            value={pool.warmup_state === "cold" ? "—" : count(sent)}
            comparison={
              pool.is_paused
                ? "a pool áll"
                : (shareOf(sent, pool.budget_today, "a napi keret") ?? look.note ?? undefined)
            }
            tone={look.tone === "running" ? "muted" : look.tone}
            trend={
              pool.warmup_state === "cold" ? undefined : (
                <LimitBar used={sent} limit={pool.budget_today} tone={look.tone} />
              )
            }
          />
          <StatTile label="Limit / IP / nap" value={count(pool.daily_limit_per_ip)} comparison={stepText(pool)} />
          <StatTile
            label={`Tegnapi bounce${yesterday.partial ? " (részleges)" : ""}`}
            value={bounce.text}
            comparison={thresholdLine("bounce", days.thresholds)}
            tone={bounce.tone}
          />
          <StatTile
            label={`Tegnapi panasz${yesterday.partial ? " (részleges)" : ""}`}
            value={complaint.text}
            comparison={thresholdLine("complaint", days.thresholds)}
            tone={complaint.tone}
          />
        </StatGrid>

        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,var(--emc-narrow-width))]">
          <Card>
            <CardHeader>
              <CardTitle>Kiment levél naponta — tény és terv</CardTitle>
              <CardDescription>
                Tömör: ami kiment, egész napokra; szaggatott: az ütemező rámpája tiszta napokkal,
                az indulástól. Logaritmikus skála: minden rácsvonal tízszeres, így egy duplázás
                mindig ugyanakkora lépés.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {points.length === 0 ? (
                <EmptyState bare title="Nincs mit rajzolni: se terv, se küldés." />
              ) : (
                <Curve
                  data={points}
                  scale="log"
                  formatValue={compact}
                  actualLabel="kiment"
                  planLabel={days.plan?.hypothetical ? "terv, ha ma indul" : "terv (a rámpa kerete)"}
                  ariaLabel={`A ${pool.code} pool napi kiküldése a tervhez képest`}
                  className="h-72"
                />
              )}
              <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-1 text-body">
                {planLines(days.plan, days.projection).map((line) => (
                  <div key={line.label} className="contents">
                    <dt className="text-muted-foreground">{line.label}</dt>
                    <dd className="m-0 font-medium">{line.value}</dd>
                  </div>
                ))}
                <dt className="text-muted-foreground">Ma eddig</dt>
                <dd className="m-0">
                  {todaySoFar(pool)}
                  {pool.warmup_state !== "cold" && (
                    <span className="text-muted-foreground"> · a pötty a legutóbbi egész nap</span>
                  )}
                </dd>
              </dl>
              <p className="text-label text-muted-foreground">
                Napok, ahogy az ütemező és a limiter számol: {dayStartText(days.today, zone)}.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tegnap levelezőnként</CardTitle>
              <CardDescription>
                {yesterday.rates === null
                  ? "Tegnapról nincs újraszámolt adat."
                  : `${count(yesterday.rates.sent)} kiment, ${dayLabel(yesterday.day)}.`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isps.length === 0 ? (
                <p className="text-muted-foreground">Nincs adat.</p>
              ) : (
                <dl className="grid grid-cols-[minmax(0,1fr)_auto_auto_auto] items-baseline gap-x-3 gap-y-1.5">
                  <span />
                  <span className="caps-label text-right">arány</span>
                  <span className="caps-label text-right">bounce</span>
                  <span className="caps-label text-right">panasz</span>
                  {isps.map((row) => (
                    <div key={row.isp} className="contents">
                      <dt title={`${count(row.sent)} levél`}>{row.label}</dt>
                      <dd className="m-0 text-right font-medium">{row.share ?? "—"}</dd>
                      <dd className="m-0 text-right text-muted-foreground">{row.bounce ?? "—"}</dd>
                      <dd className="m-0 text-right text-muted-foreground">{row.complaint ?? "—"}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Napok</CardTitle>
            <CardDescription>
              A keret az a limit, ami aznap élt; a kiment, a kézbesítve és az arányok a reggeli
              újraszámolásból jönnek (ma: élő számlálás). A döntés a nap elején születik, az előző
              nap számaiból; az indok az ütemező saját szavai.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              label={`${pool.code} — napok`}
              variant="plain"
              className="-mx-5.5 border-t border-separator"
              columns={dayColumns(minSample)}
              data={days.days}
              getRowId={(row) => row.day}
              rowClassName={(row) => (row.today ? "bg-primary-soft" : undefined)}
              empty={
                <p className="px-4 py-8 text-center text-muted-foreground">
                  A pool még nem indult: nincs egyetlen napja sem.
                </p>
              }
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Napló</CardTitle>
            <CardDescription>
              A warmup-gép és az őrszem minden lépése és a pool riasztásai, a legújabb elöl: ki,
              mikor, miért.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              label={`${pool.code} — napló`}
              variant="plain"
              className="-mx-5.5 border-t border-separator"
              columns={eventColumns()}
              data={events}
              getRowId={(event) => `${event.source}/${event.id}`}
              empty={<p className="px-4 py-8 text-center text-muted-foreground">Ez a pool még nem mozdult.</p>}
            />
          </CardContent>
        </Card>
      </div>
    </>
  );
}
